import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Users, Swords, Plus, ArrowLeft } from "lucide-react";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function BrowseCircuits() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [circuits, setCircuits] = useState([]);
  const [season, setSeason] = useState(null);

  useEffect(() => {
    loadCircuits();
  }, []);

  const loadCircuits = async () => {
    try {
      const { data } = await api.get("/arena/circuits");
      setCircuits(data.circuits);
      setSeason(data.season);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  const isVip = user?.vip && user?.vip_status === "ACTIVE";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/arena-circuit")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <PageHeader
            icon={Trophy}
            title="Browse Circuits"
            subtitle={`Active circuits in ${season?.name || "Season 1"}`}
          />
        </div>
        {isVip && (
          <button
            onClick={() => navigate("/arena-circuit/create-circuit")}
            className={btn() + " flex items-center gap-2"}
          >
            <Plus size={16} />
            Create Circuit
          </button>
        )}
      </div>

      {circuits.length === 0 ? (
        <Empty label="No Active Circuits">
          <p className="text-sm text-zinc-400 mb-4">
            No circuits are currently recruiting gladiators
          </p>
          {isVip && (
            <button
              onClick={() => navigate("/arena-circuit/create-circuit")}
              className={btn()}
            >
              <Plus size={16} className="inline mr-2" />
              Create the First Circuit
            </button>
          )}
        </Empty>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {circuits.map((circuit) => (
            <div
              key={circuit.id}
              onClick={() => navigate(`/arena-circuit/circuit/${circuit.id}`)}
              className="border border-amber-900/30 bg-zinc-950 p-5 hover:border-amber-600/50 transition-colors cursor-pointer"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-cinzel text-lg text-amber-300 tracking-wide mb-1">
                    {circuit.name}
                  </h3>
                  <p className="text-xs text-zinc-500 mb-2">
                    Organized by {circuit.editor_name}
                  </p>
                </div>
                <div className={`px-2 py-1 text-xs rounded-sm ${
                  circuit.status === "recruiting" 
                    ? "bg-emerald-900/30 border border-emerald-600/50 text-emerald-300"
                    : "bg-amber-900/30 border border-amber-600/50 text-amber-300"
                }`}>
                  {circuit.status === "recruiting" ? "Recruiting" : "Not Recruiting"}
                </div>
              </div>

              <p className="text-sm text-zinc-400 mb-4 line-clamp-2">
                {circuit.description || "No description provided"}
              </p>

              <div className="flex items-center gap-4 text-xs text-zinc-400 pt-3 border-t border-zinc-800">
                <span className="flex items-center gap-1">
                  <Users size={12} />
                  {circuit.stats?.total_fighters || 0} Gladiators
                </span>
                <span className="text-zinc-600">|</span>
                <span className="flex items-center gap-1">
                  <Swords size={12} />
                  {circuit.stats?.total_bouts || 0} Bouts
                </span>
                <span className="text-zinc-600">|</span>
                <span className="flex items-center gap-1">
                  <Trophy size={12} />
                  {circuit.stats?.fight_cards || 0} Cards
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* VIP Notice */}
      {!isVip && (
        <div className="bg-purple-950/20 border border-purple-700/40 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Trophy size={20} className="text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-cinzel text-purple-300 font-semibold mb-1">Become a Circuit Editor</h4>
              <p className="text-sm text-zinc-400 mb-2">
                Upgrade to VIP to create your own Arena Circuit and organize fight cards
              </p>
              <button
                onClick={() => navigate("/vip")}
                className={btn() + " text-sm"}
              >
                Learn More About VIP
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
